import { useEffect, useState } from "react"
import { FormProvider, useForm, useFormContext } from "react-hook-form"
import { formatNumber } from "@/lib/helpers/formatNumber"
import { useAppContext } from "@/lib/contexts/appContext"
import CheckmarkCircle from "@/common/partials/checkmarkCircle"
import Chevron from "@/common/icons/chevron"
import { Lock } from "@/common/icons/lock"
import { Checkbox } from "@/common/form/checkbox"
import { termsCheckbox } from "../payment/paymentMethod.module.css"
import useCheckoutContext, {
  useActiveStep,
  useMinAmount,
  usePaymentAmount,
  useStepByName,
  useCampaignName,
  useMembershipAmount
} from "../store"
import SubmitStepButton from "../user-details/submitStepButton"
import {
  amount as amountStyles,
  selectedAmount as selectedAmountStyles
} from "./amount.module.css"
import CustomValue from "./customValue"
import { addOrderLog } from "@/lib/api/checkoutForm"

function useCurrentAmount() {
  const {
    paymentAmount: { amountValue, hasSubmittedPaymentStep }
  } = usePaymentAmount()
  const { amounts, optional } = useActiveStep()
  let amount = amounts.find((amountObj) => amountObj.default)

  if (amountValue && (!optional || hasSubmittedPaymentStep)) {
    amount = amountValue.toString()
  } else if (amount) {
    amount = amount.amount.toString()
  } else {
    amount = amounts[0].amount.toString()
  }

  return amount
}

function useCustomValue() {
  const {
    paymentAmount: { amountValue }
  } = usePaymentAmount()
  const { amounts } = useActiveStep()

  const membershipStep = useStepByName("membershipType")

  let customValue = null

  customValue =
    amountValue &&
    !amounts.find((amountObj) => amountObj.amount === amountValue)
      ? amountValue
      : null

  if (
    [
      +membershipStep?.productPrices.family_member,
      +membershipStep?.productPrices.youth_member,
      +membershipStep?.productPrices.member
    ].includes(customValue)
  ) {
    customValue = null
  }

  return customValue || null
}

function Amount({ defaultValues, orderSessionId }) {
  const { paymentAmount, setPaymentAmount } = usePaymentAmount()
  const { amountValue, hasSubmittedPaymentStep, skipAmount } = paymentAmount
  const { amounts, allowCustomAmount, haveMembershipAmount } = useActiveStep()
  let minAmount = useMinAmount()
  const { membershipAmount } = useMembershipAmount()

  if (defaultValues?.amount) {
    minAmount = defaultValues.amount
  }

  const {
    paymentAmount: appPaymentAmount,
    setPaymentAmount: setAppPaymentAmount,
    setHasUsedAmount,
    hasUsedAmount,
    setSkipAmountStep,
    campaignName: appCampaignName
  } = useAppContext()

  const { setCampaignName } = useCampaignName()

  let amount = useCurrentAmount()
  let customValue = useCustomValue()

  const methods = useForm({
    defaultValues: {
      amount,
      customValue,
      skipAmountCheckbox: skipAmount
    },
    shouldUnregister: true
  })

  const { setNextActiveStep } = useCheckoutContext()
  const [isCustomValue, setIsCustomValue] = useState(false)
  const currentSelectedAmount = methods.watch("amount")
  const skipAmountCheckbox = methods.watch("skipAmountCheckbox")
  const customValueWatch = methods.watch("customValue")
  const [isMoreOpen, setIsMoreOpen] = useState(false)

  const visibleAmounts = amounts.filter((amountObj) => !amountObj.hidden)
  const hiddenAmounts = amounts.filter((amountObj) => amountObj.hidden)
  const showMoreSection =
    haveMembershipAmount && (hiddenAmounts.length > 0 || allowCustomAmount)

  const totalAmount = isCustomValue
    ? parseInt(customValueWatch) || 0
    : parseInt(currentSelectedAmount) || 0

  const extraAmount = totalAmount - parseInt(membershipAmount || 0)

  useEffect(() => {
    if (!isCustomValue) {
      return
    }
    methods.setValue("amount", "")
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCustomValue])

  useEffect(() => {
    if (!appPaymentAmount || hasUsedAmount) {
      return
    }
    methods.setValue("amount", appPaymentAmount, { shouldValidate: true })

    if (!amounts.find((amountObj) => +amountObj.amount === +appPaymentAmount)) {
      methods.setValue("customValue", appPaymentAmount)
      setIsCustomValue(true)
      if (haveMembershipAmount) {
        setIsMoreOpen(true)
      }
    }

    setHasUsedAmount()
    onSubmit({ amount: appPaymentAmount, customValue: appPaymentAmount })
    setAppPaymentAmount(undefined)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appPaymentAmount])

  useEffect(() => {
    if (
      isCustomValue &&
      amounts.find((amountObj) => +amountObj.amount === +currentSelectedAmount)
    ) {
      setIsCustomValue(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSelectedAmount])

  useEffect(() => {
    if (skipAmountCheckbox) {
      setIsCustomValue(false)
    }
  }, [skipAmountCheckbox])

  useEffect(() => {
    if (defaultValues.amount !== undefined) {
      methods.setValue("amount", defaultValues.amount)
      if (
        !amounts.find((amountObj) => amountObj.amount === defaultValues.amount)
      ) {
        setIsCustomValue(true)
        methods.setValue("customValue", defaultValues.amount)
        if (haveMembershipAmount) {
          setIsMoreOpen(true)
        }
      }
    }

    if (hasSubmittedPaymentStep) {
      const newValue = parseInt(amountValue)

      methods.setValue("amount", newValue)

      if (!amounts.find((amountObj) => +amountObj.amount === +newValue)) {
        setIsCustomValue(true)
        methods.setValue("customValue", newValue)
        if (haveMembershipAmount) {
          setIsMoreOpen(true)
        }
      } else if (
        haveMembershipAmount &&
        hiddenAmounts.find((amountObj) => +amountObj.amount === +newValue)
      ) {
        setIsMoreOpen(true)
      }
    } else if (haveMembershipAmount) {
      const defaultAmount =
        amounts.find((amountObj) => amountObj.default) || amounts[0]

      if (defaultAmount?.hidden) {
        setIsMoreOpen(true)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const onSubmit = ({ amount, customValue }) => {
    const selectedAmount = isCustomValue ? customValue : amount

    if (haveMembershipAmount) {
      setPaymentAmount(selectedAmount, true, true)
    } else {
      setPaymentAmount(selectedAmount, true)
    }

    addOrderLog(
      orderSessionId,
      "Valt belopp",
      isCustomValue ? customValue : amount
    )

    setCampaignName(appCampaignName)
    setSkipAmountStep(true)
    setNextActiveStep()
  }

  const deselectSkipAmountCheckbox = () => {
    methods.setValue("skipAmountCheckbox", false)
    methods.trigger("skipAmountCheckbox")
  }

  const renderMembershipAmountItem = (amountObj, key) => (
    <MembershipAmountItem
      key={key}
      amount={amountObj.amount}
      description={amountObj.description}
      subDescription={amountObj.subDescription}
      label={amountObj.label}
      isSelected={
        +currentSelectedAmount === +amountObj.amount &&
        !skipAmountCheckbox &&
        !isCustomValue
      }
      onClick={() => {
        deselectSkipAmountCheckbox()
        setIsCustomValue(false)
      }}
    />
  )

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(onSubmit)}>
        {haveMembershipAmount ? (
          <>
            <div className="flex flex-wrap flex-col mb-6 border border-b-0 rounded border-black-100/20">
              {visibleAmounts.map((amountObj, i) =>
                renderMembershipAmountItem(amountObj, i)
              )}
            </div>

            {showMoreSection && (
              <div className="mb-6 border border-black-100/20 rounded">
                <button
                  type="button"
                  className="flex items-center justify-between w-full px-4 xl:px-6 py-4 text-lg font-bold cursor-pointer text-black-100/70"
                  onClick={() => setIsMoreOpen(!isMoreOpen)}>
                  Fler val
                  <Chevron
                    className={`w-8 h-8 transition-transform duration-200 ${
                      isMoreOpen ? "transform rotate-180" : ""
                    }`}
                  />
                </button>
                {isMoreOpen && (
                  <div className="border-t border-black-100/20">
                    {hiddenAmounts.map((amountObj, i) =>
                      renderMembershipAmountItem(amountObj, `hidden-${i}`)
                    )}
                    {allowCustomAmount && (
                      <div>
                        {isCustomValue && !skipAmountCheckbox && (
                          <div className="px-4 xl:px-6 pt-4">
                            <CustomValue minAmount={minAmount} />
                          </div>
                        )}
                        {!isCustomValue && (
                          <CustomAmountOption
                            isSelected={isCustomValue && !skipAmountCheckbox}
                            onClick={() => {
                              deselectSkipAmountCheckbox()
                              setIsCustomValue(true)
                            }}
                          />
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            <div className="flex w-full flex-col bg-black-5 rounded p-4 mb-6 border border-black-100/20">
              <div className="flex w-full justify-between">
                <div>Medlemsavgift</div>
                <div>{membershipAmount} kr</div>
              </div>

              <div className="flex w-full justify-between mt-2">
                <div>Extra månadsgåva</div>
                <div>{formatNumber(extraAmount)} kr</div>
              </div>

              <div className="flex w-full justify-between mt-4 border-t border-black0 pt-4">
                <div className="font-bold">Ditt månadsbelopp</div>
                <div className="text-3xl font-bold">
                  {formatNumber(totalAmount)} kr
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2 mb-6 text-sm text-black-100/70">
              <Lock />
              <span>
                Du kan alltid ändra ditt stöd eller avsluta när du vill
              </span>
            </div>
          </>
        ) : (
          <>
            <div className="flex flex-wrap mb-6 font-bold border divide-y rounded border-black-100/20 divide lg:divide-y-0 lg:divide-x">
              {amounts.map((amountObj, i) => (
                <AmountItem
                  key={i}
                  amount={amountObj.amount}
                  isSelected={
                    +currentSelectedAmount === +amountObj.amount &&
                    !skipAmountCheckbox
                  }
                  onClick={() => deselectSkipAmountCheckbox()}
                />
              ))}
            </div>
            {allowCustomAmount && (
              <div className="mb-6">
                {isCustomValue && !skipAmountCheckbox && (
                  <CustomValue minAmount={minAmount} />
                )}
                <label
                  htmlFor="customValue"
                  className={`flex items-center px-4 xl:px-6 py-4 text-lg font-bold border border-black-100/20 rounded cursor-pointer group ${
                    isCustomValue && !skipAmountCheckbox ? "hidden" : ""
                  }`}
                  onClick={() => {
                    deselectSkipAmountCheckbox()
                    setIsCustomValue(true)
                  }}>
                  <CheckmarkCircle />
                  Valfritt
                </label>
              </div>
            )}
          </>
        )}

        <SubmitStepButton />
      </form>
    </FormProvider>
  )
}

export function SkipAmount() {
  const { register, watch } = useFormContext()
  const skipAmount = watch("skipAmountCheckbox")
  return (
    <label
      htmlFor="skipAmountCheckbox"
      className="flex items-center cursor-pointer select-none mb-7">
      <input
        type="checkbox"
        {...register("skipAmountCheckbox")}
        id="skipAmountCheckbox"
        className={`${termsCheckbox} absolute -z-1`}
      />
      <Checkbox
        checked={
          skipAmount === true ||
          (skipAmount?.length === 1 && skipAmount[0] === "on")
        }
      />
      <p className="text-sm md:text-base wysiwyg-content">
        Jag vill inte ge en gåva
      </p>
    </label>
  )
}

function CustomAmountOption({ isSelected, onClick }) {
  return (
    <div
      role="button"
      tabIndex={0}
      className={[
        "w-full flex flex-grow px-4 xl:px-6 py-4 cursor-pointer items-center text-lg group border-black-100/20",
        isSelected
          ? `amount-label bg-green-25 color-white selected-amount relative ${selectedAmountStyles}`
          : "amount-label"
      ].join(" ")}
      onClick={onClick}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault()
          onClick()
        }
      }}>
      <CheckmarkCircle selected={isSelected} />
      <div className="flex flex-col flex-grow ml-3">
        <div className="font-bold">Välj eget belopp</div>
        <div className="text-sm font-normal text-black-100/70">
          Skriv in ett valfritt totalbelopp
        </div>
      </div>
    </div>
  )
}

export function MembershipAmountItem({
  amount,
  description,
  subDescription,
  label,
  isSelected,
  id = "amount",
  onClick = () => {}
}) {
  const { register } = useFormContext()

  const total = parseInt(amount)

  const isSelectedClassNames = isSelected
    ? `amount-label bg-green-25 color-white selected-amount relative ${selectedAmountStyles}`
    : "amount-label"

  return (
    <label
      htmlFor={`${id}-${amount}`}
      className={[
        "w-full lg:w-auto flex flex-grow border-b px-4 xl:px-6 py-4 cursor-pointer items-center text-lg group border-black-100/20",
        isSelectedClassNames
      ].join(" ")}>
      <input
        type="radio"
        id={`${id}-${amount}`}
        className={`${amountStyles} absolute -z-1`}
        {...register("amount")}
        defaultValue={amount}
        onClick={onClick}
      />
      <CheckmarkCircle selected={isSelected} />
      <div className="flex flex-col flex-grow ml-3">
        <div className="flex flex-wrap items-center gap-4">
          <span className="font-bold">{formatNumber(total)} kr/månad</span>
          {label && (
            <span className="text-sm font-bold bg-green-50 rounded px-2 py-0.5">
              {label}
            </span>
          )}
        </div>
        {description && <div className="font-bold text-sm">{description}</div>}
        {subDescription && (
          <div className="mt-1 text-sm font-normal text-black-100/70">
            {subDescription}
          </div>
        )}
      </div>
    </label>
  )
}

export function AmountItem({
  amount,
  isSelected,
  id = "amount",
  customText = false,
  onClick = () => {}
}) {
  const { register } = useFormContext()

  const isSelectedClassNames = isSelected
    ? `amount-label bg-green-25 color-white selected-amount relative ${selectedAmountStyles}`
    : "amount-label"

  return (
    <label
      htmlFor={`${id}-${amount}`}
      className={[
        "w-full lg:w-auto flex flex-grow px-4 xl:px-6 py-4 cursor-pointer items-center text-lg group border-black-100/20",
        isSelectedClassNames
      ].join(" ")}>
      <input
        type="radio"
        id={`${id}-${amount}`}
        className={`${amountStyles} absolute -z-1`}
        {...register("amount")}
        defaultValue={amount}
        onClick={onClick}
      />
      <CheckmarkCircle selected={isSelected} />
      {customText && <>{customText}</>}

      {!customText && <>{formatNumber(amount)} kr</>}
    </label>
  )
}

export default Amount
